import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AlertTriangle, Award, BadgeCheck, Bug, CheckCircle2, Clapperboard, Gamepad2, Gauge, Globe, GraduationCap, Hand, Medal, Megaphone, Monitor, Server, Shapes,
  Smartphone, Tv, Users,
} from 'lucide-react';
import {
  JOB_CATEGORIES,
  type CycleSourcing, type EarningStatus, type IneligibleReason, type JobCategory, type JobPricing, type ParticipantStatus, type PayoutAccountStatus,
  type Platform, type RecordingMode, type StatementStatus, type TesterLevel, type TesterStatsDto,
} from '@/api/types';
import { Badge, type Tone } from '@/components/ui/Badge';
import { formatMoney } from '@/lib/format';
import { cn } from '@/lib/cn';

export function Money({ value, currency = 'TRY', className }: { value: number | null | undefined; currency?: string; className?: string }) {
  const { i18n } = useTranslation();
  return <span className={cn('tabular whitespace-nowrap', className)}>{formatMoney(value, i18n.language, currency)}</span>;
}

/** The pay of a job as short chips: per accepted run, bug bounties (range), fixed fee. */
export function PricingChips({ pricing, className }: { pricing: JobPricing; className?: string }) {
  const { t, i18n } = useTranslation();
  const m = (v: number) => formatMoney(v, i18n.language, pricing.currency);
  const bugs = [pricing.bugTrivial, pricing.bugMinor, pricing.bugMajor, pricing.bugCritical].filter((v): v is number => !!v && v > 0);
  const items: string[] = [];
  if (pricing.perAcceptedRun) items.push(t('market.pay.perRun', { amount: m(pricing.perAcceptedRun) }));
  if (bugs.length) items.push(bugs.length === 1 ? t('market.pay.bug', { amount: m(bugs[0]) }) : t('market.pay.bugRange', { min: m(Math.min(...bugs)), max: m(Math.max(...bugs)) }));
  if (pricing.fixedFee) items.push(t('market.pay.fixed', { amount: m(pricing.fixedFee) }));
  if (items.length === 0) return <span className={cn('text-xs text-ink-3', className)}>{t('market.pay.unpaid')}</span>;
  return (
    <ul className={cn('flex flex-wrap gap-1.5', className)}>
      {items.map((i) => (
        <li key={i} className="rounded-[3px] border border-trusted/30 bg-trusted-soft px-1.5 py-0.5 text-xs font-medium text-trusted">{i}</li>
      ))}
    </ul>
  );
}

/** Full pay table (job detail, wizard review). */
export function PricingTable({ pricing }: { pricing: JobPricing }) {
  const { t } = useTranslation();
  const rows: [string, number | null][] = [
    [t('market.pay.rowPerRun'), pricing.perAcceptedRun],
    [t('market.pay.rowBug', { severity: t('severity.Critical') }), pricing.bugCritical],
    [t('market.pay.rowBug', { severity: t('severity.Major') }), pricing.bugMajor],
    [t('market.pay.rowBug', { severity: t('severity.Minor') }), pricing.bugMinor],
    [t('market.pay.rowBug', { severity: t('severity.Trivial') }), pricing.bugTrivial],
    [t('market.pay.rowFixed'), pricing.fixedFee],
  ];
  const shown = rows.filter(([, v]) => v !== null && v > 0);
  if (shown.length === 0) return <p className="text-sm text-ink-3">{t('market.pay.unpaid')}</p>;
  const bounty = [pricing.bugCritical, pricing.bugMajor, pricing.bugMinor, pricing.bugTrivial].some((v) => !!v && v > 0);
  return (
    <div className="flex flex-col gap-2">
      <dl className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-4 gap-y-1.5 text-sm">
        {shown.map(([label, value]) => (
          <div key={label} className="contents">
            <dt className="text-ink-2">{label}</dt>
            <dd className="text-right font-semibold"><Money value={value} currency={pricing.currency} /></dd>
          </div>
        ))}
      </dl>
      {bounty && <p className="text-xs text-ink-3" data-testid="first-reporter-rule">{t('market.pay.firstReporter')}</p>}
    </div>
  );
}

const SOURCING_ICON: Record<CycleSourcing, ReactNode> = { Community: <Users />, Applications: <Megaphone />, OpenPool: <Hand /> };

export function SourcingBadge({ sourcing }: { sourcing: CycleSourcing }) {
  const { t } = useTranslation();
  return <Badge tone={sourcing === 'Community' ? 'neutral' : 'info'} icon={SOURCING_ICON[sourcing]}>{t(`sourcing.${sourcing}`)}</Badge>;
}

const PARTICIPANT_TONE: Record<ParticipantStatus, Tone> = {
  Applied: 'check', Invited: 'info', Accepted: 'trusted', Rejected: 'suspicious', Withdrawn: 'muted', Removed: 'muted',
};

export function ParticipantStatusBadge({ status }: { status: ParticipantStatus }) {
  const { t } = useTranslation();
  return <Badge tone={PARTICIPANT_TONE[status]}>{t(`participantStatus.${status}`)}</Badge>;
}

const EARNING_TONE: Record<EarningStatus, Tone> = { Pending: 'check', Invoiced: 'info', Paid: 'accent', PaidOut: 'trusted', Void: 'muted' };

export function EarningStatusBadge({ status }: { status: EarningStatus }) {
  const { t } = useTranslation();
  return <Badge tone={EARNING_TONE[status]}>{t(`earningStatus.${status}`)}</Badge>;
}

const STATEMENT_TONE: Record<StatementStatus, Tone> = { Open: 'check', Paid: 'trusted', Cancelled: 'muted' };

export function StatementStatusBadge({ status }: { status: StatementStatus }) {
  const { t } = useTranslation();
  return <Badge tone={STATEMENT_TONE[status]}>{t(`statementStatus.${status}`)}</Badge>;
}

export function PayoutStatusBadge({ status }: { status: PayoutAccountStatus }) {
  const { t } = useTranslation();
  return <Badge tone={status === 'Ready' ? 'trusted' : status === 'Failed' ? 'suspicious' : 'muted'}>{t(`payoutStatus.${status}`)}</Badge>;
}

/** Eligibility: a green tick, or the reasons a tester cannot take the job. */
export function Eligibility({ eligible, reasons, className }: { eligible: boolean; reasons: IneligibleReason[]; className?: string }) {
  const { t } = useTranslation();
  if (eligible) {
    return (
      <span className={cn('inline-flex items-center gap-1 text-xs font-medium text-trusted', className)}>
        <CheckCircle2 className="size-3.5" /> {t('market.eligible')}
      </span>
    );
  }
  return (
    <span className={cn('inline-flex flex-wrap items-center gap-1 text-xs font-medium text-check', className)}>
      <AlertTriangle className="size-3.5" />
      {reasons.map((r) => t(`ineligible.${r}`)).join(' · ')}
    </span>
  );
}

/** Reputation, accepted runs, confirmed bugs and average confidence in one line. */
const LEVEL_STYLE: Record<TesterLevel, string> = {
  New: 'border-line-strong text-ink-3',
  Bronze: 'border-[#b0723f]/50 bg-[#b0723f]/10 text-[#9a5d2c] dark:text-[#d79a66]',
  Silver: 'border-[#7d8794]/50 bg-[#7d8794]/10 text-[#5d6672] dark:text-[#b7c0cc]',
  Gold: 'border-[#c49a10]/50 bg-[#c49a10]/10 text-[#8a6a00] dark:text-[#e6c24a]',
  Platinum: 'border-[#2b8a8a]/50 bg-[#2b8a8a]/10 text-[#1f6d6d] dark:text-[#6fd0d0]',
};

/** "Gümüş" etc. Levels follow reviewed work, the academy and the identity check. */
export function LevelBadge({ level, className }: { level: TesterLevel; className?: string }) {
  const { t } = useTranslation();
  return (
    <span className={cn('inline-flex items-center gap-1 rounded-[3px] border px-1.5 py-px text-[11px] font-semibold', LEVEL_STYLE[level], className)} title={t('level.hint')}>
      <Medal className="size-3" aria-hidden />{t(`level.${level}`)}
    </span>
  );
}

/** A checked identity (by a platform admin, or through the card payout provider). */
export function VerifiedMark({ className }: { className?: string }) {
  const { t } = useTranslation();
  return (
    <span className={cn('inline-flex items-center gap-0.5 text-xs font-medium text-trusted', className)} title={t('level.verifiedHint')}>
      <BadgeCheck className="size-3.5" aria-hidden />{t('level.verified')}
    </span>
  );
}

export function StatsLine({ stats, className, level = true }: { stats: TesterStatsDto; className?: string; level?: boolean }) {
  const { t } = useTranslation();
  return (
    <span className={cn('inline-flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-ink-2 [&_svg]:size-3.5 [&_svg]:text-ink-3', className)}>
      {level && <LevelBadge level={stats.level} className="[&_svg]:!size-3 [&_svg]:!text-current" />}
      {level && stats.identityVerified && <VerifiedMark className="[&_svg]:!text-trusted" />}
      <span className="inline-flex items-center gap-1" title={t('market.stats.reputation')}><Award /> <span className="mono">{stats.reputation}</span></span>
      <span className="inline-flex items-center gap-1" title={t('market.stats.acceptedRuns')}><CheckCircle2 /> <span className="mono">{stats.acceptedRuns}</span></span>
      <span className="inline-flex items-center gap-1" title={t('market.stats.confirmedBugs')}><Bug /> <span className="mono">{stats.confirmedBugs}</span></span>
      {stats.averageConfidence !== null && (
        <span className="inline-flex items-center gap-1" title={t('market.stats.averageConfidence')}><Gauge /> <span className="mono">{stats.averageConfidence}</span></span>
      )}
    </span>
  );
}

/** Small labelled number tile. */
export function Tile({ label, value, tone, hint }: { label: string; value: ReactNode; tone?: 'trusted' | 'check' | 'accent'; hint?: string }) {
  return (
    <div className="panel flex flex-col gap-1 p-4" title={hint}>
      <span className="text-[11px] font-semibold uppercase tracking-[0.04em] text-ink-3">{label}</span>
      <span className={cn('text-xl font-semibold', tone === 'trusted' && 'text-trusted', tone === 'check' && 'text-check', tone === 'accent' && 'text-accent-text')}>{value}</span>
      {hint && <span className="text-xs text-ink-3">{hint}</span>}
    </div>
  );
}

const CATEGORY_ICON: Record<JobCategory, ReactNode> = {
  MobileApp: <Smartphone />, WebApp: <Globe />, DesktopApp: <Monitor />, Game: <Gamepad2 />, Api: <Server />, SmartDevice: <Tv />, Other: <Shapes />,
};

/** The category a project's platform suggests (same rule as the API). */
export const categoryForPlatform = (platform: Platform): JobCategory => (platform === 'Web' ? 'WebApp' : 'MobileApp');

/** Read-only category labels with icons (job cards, profiles). */
export function CategoryTags({ categories, className }: { categories: readonly JobCategory[]; className?: string }) {
  const { t } = useTranslation();
  if (categories.length === 0) return null;
  return (
    <ul className={cn('flex flex-wrap gap-x-3 gap-y-1 text-xs text-ink-2', className)} aria-label={t('market.categories')}>
      {categories.map((c) => (
        <li key={c} className="inline-flex items-center gap-1 [&>svg]:size-3.5 [&>svg]:text-ink-3">{CATEGORY_ICON[c]}{t(`jobCategory.${c}`)}</li>
      ))}
    </ul>
  );
}

const chip = (on: boolean) =>
  cn(
    'inline-flex h-8 shrink-0 items-center gap-1.5 rounded-[var(--radius-ctl)] border px-2.5 text-sm transition-colors [&>svg]:size-4',
    'disabled:cursor-not-allowed disabled:opacity-45',
    on ? 'border-accent bg-accent-soft font-medium text-accent-text [&>svg]:text-accent-text' : 'border-line-strong bg-surface text-ink-2 hover:border-ink-3 [&>svg]:text-ink-3',
  );

/** Multi-select category chips (wizard, profile). With `max`, further chips are disabled once the limit is reached. */
export function CategoryPicker({ value, onChange, label, max, className }: {
  value: readonly JobCategory[]; onChange: (next: JobCategory[]) => void; label: string; max?: number; className?: string;
}) {
  const { t } = useTranslation();
  const full = max !== undefined && value.length >= max;
  const toggle = (c: JobCategory) =>
    onChange(value.includes(c) ? value.filter((x) => x !== c) : JOB_CATEGORIES.filter((x) => x === c || value.includes(x)));
  return (
    <div role="group" aria-label={label} className={cn('flex flex-col gap-2', className)}>
      <div className="flex flex-wrap gap-2">
        {JOB_CATEGORIES.map((c) => {
          const on = value.includes(c);
          return (
            <button key={c} type="button" aria-pressed={on} disabled={!on && full} className={chip(on)} onClick={() => toggle(c)}>
              {CATEGORY_ICON[c]}{t(`jobCategory.${c}`)}
            </button>
          );
        })}
      </div>
      {max !== undefined && (
        <p className={cn('text-xs', full ? 'text-ink-2' : 'text-ink-3')}>{t('market.categoryLimit', { count: value.length, max })}</p>
      )}
    </div>
  );
}

/** Single-select filter row: "all" plus every category; scrolls sideways on narrow screens. */
export function CategoryFilter({ value, onChange, label }: { value: JobCategory | null; onChange: (next: JobCategory | null) => void; label: string }) {
  const { t } = useTranslation();
  return (
    <div role="group" aria-label={label} className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1">
      <button type="button" aria-pressed={value === null} className={chip(value === null)} onClick={() => onChange(null)}>
        {t('market.allCategories')}
      </button>
      {JOB_CATEGORIES.map((c) => (
        <button key={c} type="button" aria-pressed={value === c} className={chip(value === c)} onClick={() => onChange(value === c ? null : c)}>
          {CATEGORY_ICON[c]}{t(`jobCategory.${c}`)}
        </button>
      ))}
    </div>
  );
}

/** Screen-video jobs: the tester needs to record the phone's screen. Web (SDK) jobs show nothing. */
export function RecordingModeBadge({ mode, className }: { mode: RecordingMode; className?: string }) {
  const { t } = useTranslation();
  if (mode !== 'ScreenVideo') return null;
  return <Badge tone="info" icon={<Clapperboard className="size-3.5" />} className={className} title={t('recordingMode.ScreenVideoHint')}>{t('recordingMode.badge')}</Badge>;
}

/** The TestRome academy job (free practice; an accepted run qualifies the tester). */
export function AcademyBadge({ className }: { className?: string }) {
  const { t } = useTranslation();
  return <Badge tone="accent" icon={<GraduationCap className="size-3.5" />} className={className} title={t('level.academyHint')}>{t('level.academy')}</Badge>;
}
